import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { UserPackagesReportsState, QueryParams } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: UserPackagesReportsState = {
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'userPackagesReports',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
  },
});

export const api = createApi({
  reducerPath: 'userPackagesReportsApi',
  tagTypes: ['userPackages', 'analytics'],
  baseQuery,
  endpoints: builder => ({
    // Main comprehensive API
    getUserPackages: builder.query<any, QueryParams>({
      query: params => ({
        url: endpoints.USER_PACKAGES,
        method: 'GET',
        params,
      }),
      providesTags: ['userPackages'],
    }),

    // Get packages list without analytics
    getUserPackagesList: builder.query<any, QueryParams>({
      query: params => ({
        url: `${endpoints.USER_PACKAGES}/list`,
        method: 'GET',
        params,
      }),
      providesTags: ['userPackages'],
    }),

    // Get analytics only
    getAnalytics: builder.query<any, { date_from?: string; date_to?: string }>({
      query: params => ({
        url: `${endpoints.USER_PACKAGES}/analytics`,
        method: 'GET',
        params,
      }),
      providesTags: ['analytics'],
    }),

    // Get expired packages
    getExpiredPackages: builder.query<
      any,
      {
        page?: number;
        limit?: number;
        days_expired?: number;
        search?: string;
      }
    >({
      query: params => ({
        url: `${endpoints.USER_PACKAGES}/expired`,
        method: 'GET',
        params,
      }),
      providesTags: ['userPackages'],
    }),

    // Get bandwidth usage report
    getBandwidthUsage: builder.query<
      any,
      {
        page?: number;
        limit?: number;
        usage_level?: string;
        search?: string;
      }
    >({
      query: params => ({
        url: `${endpoints.USER_PACKAGES}/bandwidth-usage`,
        method: 'GET',
        params,
      }),
      providesTags: ['userPackages'],
    }),

    // Export to Excel
    exportToExcel: builder.query<any, QueryParams>({
      query: params => ({
        url: `${endpoints.USER_PACKAGES}/export/excel`,
        method: 'GET',
        params,
        responseHandler: response => response.blob(),
      }),
    }),

    // Get package types for filtering
    getPackageTypes: builder.query<any, void>({
      query: () => ({
        url: `${endpoints.USER_PACKAGES}/package-types`,
        method: 'GET',
      }),
    }),
  }),
});

export const { actions: UserPackagesReportsActions } = slice;

export const useUserPackagesReportsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: slice.actions,
    useGetUserPackagesQuery: api.useLazyGetUserPackagesQuery,
    useGetUserPackagesListQuery: api.useLazyGetUserPackagesListQuery,
    useGetAnalyticsQuery: api.useLazyGetAnalyticsQuery,
    useGetExpiredPackagesQuery: api.useLazyGetExpiredPackagesQuery,
    useGetBandwidthUsageQuery: api.useLazyGetBandwidthUsageQuery,
    useExportToExcelQuery: api.useLazyExportToExcelQuery,
    useGetPackageTypesQuery: api.useLazyGetPackageTypesQuery,
  };
};
